"use client";

import { Paper } from "@mui/material";
import { useMemo } from "react";

import { Language } from "@holibob-packages/language";

import type { OnBookingCompletedCallback, ProductData } from "../apiHooks";
import { ErrorBoundary } from "../components";
import { useLanguage } from "../hooks/useLanguage";
import { ProductFull as ProductFullComponent, ProductProps as ProductFullComponentProps } from "../product/ProductFull";
import { ProductContent, ProductContentEntry, ProductMeta as ProductMetaType } from "../product/types";
import { Price } from "../types/Price";
import { ProductContentType } from "../types/ProductContent";
import { BookingConsumerTripInput } from "../types/ProductFullLoaded";
import { durationFromString } from "../utils/formatters/formatDuration";
import { Availability } from "./Availability";

export type ProductFullLoadedProps = {
    product: ProductData;
    bookingReference?: string;
    onBookingCompleted?: OnBookingCompletedCallback;
    onAvailabilityChange?: (availability: string | null) => void;
    showHeader?: boolean;
    constrainWidth?: boolean;
    showAvailability?: boolean;
    withStickyAppBar?: boolean;
    withSectionFadeUpAnimation?: boolean;
    showFavouriteButton?: boolean;
    isFavourite?: boolean;
    onFavouriteChange?: (isFavourite: boolean) => void;
    consumerTrip?: BookingConsumerTripInput;
} & Pick<ProductFullComponentProps, "belowHeaderElement" | "layoutMode"> & { children?: React.ReactNode };

export function ProductFullLoaded({
    product,
    onBookingCompleted,
    belowHeaderElement,
    bookingReference,
    onAvailabilityChange,
    layoutMode,
    showHeader = true,
    constrainWidth = true,
    showAvailability = true,
    withStickyAppBar = false,
    withSectionFadeUpAnimation = false,
    showFavouriteButton,
    isFavourite,
    onFavouriteChange,
    consumerTrip,
    children,
}: ProductFullLoadedProps) {
    const language = useLanguage();

    const content = useMemo(() => {
        return createProductContent(product);
    }, [product]);

    const meta = useMemo(() => {
        return createProductMeta(product, language);
    }, [product, language]);

    const sideElement = showAvailability ? (
        <AvailabilitySection
            product={product}
            onBookingCompleted={onBookingCompleted}
            bookingReference={bookingReference}
            onAvailabilityChange={onAvailabilityChange}
            consumerTrip={consumerTrip}
        />
    ) : null;

    const imageList = product.images.map((x) => x.urlLarge);
    const minPrice: Price | undefined = product.holibobGuidePrice
        ? {
              amount: product.holibobGuidePrice.gross,
              currency: product.holibobGuidePrice.currency,
          }
        : undefined;

    return (
        <ProductFullComponent
            title={product.name ?? ""}
            imageList={imageList}
            minPrice={minPrice}
            content={content}
            belowHeaderElement={belowHeaderElement}
            meta={meta}
            attributeList={product.attributeList.nodes}
            additions={product.additionList.nodes}
            cancellationPolicy={product.cancellationPolicy}
            reviewRating={product.reviewRating}
            reviewCount={product.reviewCount}
            layoutMode={layoutMode}
            showHeader={showHeader}
            constrainWidth={constrainWidth}
            sideElement={sideElement}
            withStickyAppBar={withStickyAppBar}
            withSectionFadeUpAnimation={withSectionFadeUpAnimation}
            showFavouriteButton={showFavouriteButton}
            isFavourite={isFavourite}
            onFavouriteChange={onFavouriteChange}
        >
            {children}
        </ProductFullComponent>
    );
}

type AvailabilitySectionProps = {
    product: ProductData;
    onBookingCompleted?: OnBookingCompletedCallback;
    bookingReference?: string;
    onAvailabilityChange?: (availability: string | null) => void;
    consumerTrip?: BookingConsumerTripInput;
};
const AvailabilitySection = (props: AvailabilitySectionProps) => {
    const { product, consumerTrip, onBookingCompleted, bookingReference, onAvailabilityChange } = props;

    return (
        <Paper elevation={1}>
            <ErrorBoundary>
                <Availability
                    product={product}
                    onBookingCompleted={onBookingCompleted}
                    bookingReference={bookingReference}
                    onAvailabilityChange={(availabilityListItemId) => {
                        if (onAvailabilityChange) {
                            onAvailabilityChange(availabilityListItemId);
                        }
                    }}
                    consumerTrip={consumerTrip}
                />
            </ErrorBoundary>
        </Paper>
    );
};

function createProductContent(product: ProductData) {
    return {
        root: product.description ? { name: product.name, description: product.description } : undefined,
        other: computeOtherProductContent(product.contentList.nodes),
    } as ProductContent;
}

export function computeOtherProductContent(productContentNodes: Readonly<ProductData["contentList"]["nodes"]>) {
    const nodes = productContentNodes.slice().sort((a, b) => {
        return (a.ordinalPosition ?? 0) - (b.ordinalPosition ?? 0);
    });

    const other = new Map<ProductContentType, ProductContentEntry[]>();
    for (const entry of nodes) {
        let description = entry.description ?? "";
        let name = entry.name ?? "";

        if (!name && !description) {
            continue;
        }

        // if description is not defined than name is treated as discription for styling purposes
        if (!description) {
            description = `<p>${name}</p>`;
            name = "";
        }

        const entryType = entry.type as ProductContentType;
        let tab = other.get(entryType);
        if (!tab) {
            other.set(entryType, (tab = []));
        }
        tab.push({ name, description, descriptionJson: [] });
    }

    return other;
}

function optionalTransform<T>(value: string | undefined, transform: (value: string) => T): T | undefined {
    if (value) {
        return transform(value);
    }
    return;
}

export function createProductMeta(product: ProductData, language: Language): ProductMetaType {
    const metaIndexed = product.metaList.nodes.reduce((acc, node) => {
        if (node.type && node.value) {
            acc.set(node.type, node.value);
        }
        return acc;
    }, new Map<string, string>());

    return {
        minDuration: product.minDuration,
        maxDuration: product.maxDuration,
        startTime: metaIndexed.get("START_TIME"),
        fitnessLevel: metaIndexed.get("FITNESS_LEVEL"),
        difficultyLevel: metaIndexed.get("DIFFICULTY_LEVEL"),
        walkingDistance: optionalTransform(metaIndexed.get("WALKING_DISTANCE_KILOMETERS"), (x) => {
            const result = parseFloat(x);
            if (Number.isNaN(result)) {
                return;
            }
            return result;
        }),
        cultureShockLevel: product.cultureShockLevel,
        bestTimeToVisit: product.bestTimeToVisit,
        maxGroupSize: product.maxGroupSize,
        guideLanguages: product.guideLanguageList.nodes,
        advanceArrivalDuration: product.advanceArrivalDuration
            ? durationFromString(language, product.advanceArrivalDuration)
            : undefined,
        startTimes: product.startTimeList.nodes,
    };
}
